import { assign, createMachine } from 'xstate';
import { billingMachine, billingContext } from '~/machines/billing-selection';
import { enrollmentMachine, enrollmentContext } from '~/machines/enrollment-selection';
import { userContext, userMachine } from '~/machines/user-creation';
import { utilityContext, utilityMachine } from '~/machines/utility-selection';
import { LDClient, LDFlags } from '~/providers';
import {
  fetchBillpayEligibility,
  fetchMembershipPromoCode,
  redirectToDashboard,
  signUpUser,
} from '~/services/';
import { BillingDataSource, EnrollmentDataSource } from '~/typings/context';
import { removeKeys } from '~/utils/for-warbler';
import { membershipFunnelRedirects } from '~/utils/redirects';
import { identify, sendToSegment } from '~/utils/tracking';
import {
  MembershipFunnelContext,
  MembershipFunnelEvent,
  MembershipFunnelParams,
  MembershipFunnelServices,
} from './types';

export const membershipMachine = (params?: MembershipFunnelParams) =>
  createMachine(
    {
      tsTypes: {} as import('./machine.typegen').Typegen0,
      predictableActionArguments: true,
      schema: {
        context: {} as MembershipFunnelContext,
        events: {} as MembershipFunnelEvent,
        services: {} as MembershipFunnelServices,
      },
      context: {
        queryParams: {},
      },
      id: params?.id || 'membership',
      initial: params?.initialState || 'start',
      states: {
        start: {
          always: [
            { cond: 'isBlockSignupWhenResuming', target: 'csUnavailable' },
            {
              cond: 'isResuming',
              target: 'fetchPromoData',
              actions: ['setResumingData', 'identifyResumingUserForTracking'],
            },
            { target: 'utility' },
          ],
        },
        utility: {
          invoke: {
            id: 'utilityMachine',
            src: 'utilityMachine',
            data: utilityContext(funnelContext => ({
              queryParams: funnelContext.queryParams,
              redirect: membershipFunnelRedirects,
            })),
            onDone: [
              {
                cond: 'isSignupUnavailable',
                target: 'csUnavailable',
                actions: 'setUtilitySelection',
              },
              { target: 'user', actions: 'setUtilitySelection' },
            ],
          },
        },
        user: {
          invoke: {
            id: 'userMachine',
            src: 'userMachine',
            data: userContext(funnelContext => ({
              funnelId: 'membership',
              queryParams: funnelContext.queryParams,
              utility: funnelContext.utilitySelection!,
              zipCode: funnelContext.zipCode!,
            })),
            onDone: { target: 'fetchPromoData', actions: 'setFunnelPacket' },
          },
          on: {
            CHANGE_UTILITY: { target: 'utility', actions: 'clearUtilitySelection' },
          },
        },
        fetchPromoData: {
          invoke: {
            id: 'fetchPromoCode',
            src: 'fetchPromoCode',
            onDone: [
              {
                cond: 'hasVerifiedCredentialsOnResuming',
                target: 'billingExperience',
                actions: ['setEnrollmentAsUtilityConnect', 'setBillingAsAcb', 'setPromoData'],
              },
              { target: 'fetchEnrollmentOptions', actions: 'setPromoData' },
            ],
            onError: 'fetchEnrollmentOptions',
          },
        },
        fetchEnrollmentOptions: {
          invoke: {
            id: 'fetchEnrollmentOptions',
            src: 'fetchEnrollmentOptions',
            onDone: [
              { cond: 'canConnectUtility', target: 'enrollment' },
              { target: 'billing', actions: ['setEnrollmentAsNone', 'setBillingAsUcb'] },
            ],
            onError: 'unexpected',
          },
        },
        enrollment: {
          invoke: {
            id: 'enrollmentMachine',
            src: 'enrollmentMachine',
            data: enrollmentContext(funnelContext => ({
              canSkipUtilityConnect: true,
              funnelPacket: funnelContext.funnelPacket!,
              enrollmentOptions: ['utility_connect'],
            })),
            onDone: [
              {
                cond: 'didConnectUtility',
                target: 'billingExperience',
                actions: ['setEnrollmentAsUtilityConnect', 'setBillingAsAcb'],
              },
              { target: 'billing', actions: ['setEnrollmentAsNone', 'setBillingAsUcb'] },
            ],
          },
        },
        billingExperience: {
          on: {
            SELECTED_BILLING_OPTION: { target: 'billing', actions: 'setBillingSelection' },
          },
        },
        billing: {
          invoke: {
            id: 'billingMachine',
            src: 'billingMachine',
            data: billingContext(funnelContext => ({
              billingOptions: [funnelContext.billingSelection!],
              funnelPacket: funnelContext.funnelPacket!,
            })),
            onDone: 'completed',
          },
        },
        completed: {
          id: 'completed',
          initial: 'assigning',
          states: {
            assigning: {
              invoke: {
                id: 'signUpUser',
                src: 'signUpUser',
                onDone: 'redirecting',
                onError: '#unexpected',
              },
            },
            redirecting: {
              type: 'final',
              onEntry: ['trackFunnelCompleted', 'redirectToDashboard'],
            },
          },
        },
        unexpected: {
          id: 'unexpected',
        },
        csUnavailable: {
          id: 'csUnavailable',
        },
      },
    },
    {
      actions: {
        /* eslint-disable @typescript-eslint/no-unused-vars */
        // TLDR: need the unused _context - https://xstate.js.org/docs/guides/typescript.html#troubleshooting
        clearUtilitySelection: assign(context => ({
          utilitySelection: undefined,
          queryParams: removeKeys(context.queryParams, ['utilityId', 'zipCode']),
        })),
        identifyResumingUserForTracking: context => {
          const { user, utility } = context.queryParams.resumingUser!.packet;
          identify({
            id: user.id,
            email: user.email,
            utilityId: utility.id,
            funnelTrack: utility.funnelTrack,
            state: utility.state.abbreviation,
          });
        },
        redirectToDashboard: context => {
          redirectToDashboard({
            userId: context.funnelPacket!.user.id,
            utilityAccountId: context.funnelPacket!.utilityAccount.id,
            billingExperienceType: context.billingSelection,
          });
        },
        setBillingSelection: assign((_context, event) => ({
          billingSelection: event.data.billingSelection,
        })),
        setBillingAsAcb: assign(_context => ({
          billingSelection: 'acb' as BillingDataSource,
        })),
        setBillingAsUcb: assign(_context => ({
          billingSelection: 'ucb' as BillingDataSource,
        })),
        setEnrollmentAsUtilityConnect: assign(_context => ({
          enrollmentSelection: 'utility_connect' as EnrollmentDataSource,
        })),
        setEnrollmentAsNone: assign(_context => ({
          enrollmentSelection: 'none' as EnrollmentDataSource,
        })),
        setFunnelPacket: assign((_context, event) => ({
          funnelPacket: event.data.funnelPacket,
        })),
        setPromoData: assign((_context, event) => ({
          sponsored: event.data.sponsored,
          sponsoredBy: event.data.sponsoredBy,
        })),
        setUtilitySelection: assign((_context, event) => ({
          utilitySelection: event.data.utility,
          zipCode: event.data.zipCode,
        })),
        setResumingData: assign(context => ({
          funnelPacket: context.queryParams.resumingUser!.packet,
        })),
        trackFunnelCompleted: context => {
          sendToSegment({
            type: 'funnel_completed',
            funnelPacket: context.funnelPacket!,
            track: 'membership',
          });
        },
        /* eslint-disable @typescript-eslint/no-unused-vars */
      },
      guards: {
        canConnectUtility: (_context, event) =>
          event.data.enrollmentOptions.includes('utility_connect'),
        didConnectUtility: (_context, event) =>
          event.data.enrollmentSelection === 'utility_connect',
        hasVerifiedCredentialsOnResuming: context =>
          context.funnelPacket!.utilityCredential.verificationStatus! === 'correct',
        isResuming: context => !!context.queryParams?.resumingUser,
        isBlockSignupWhenResuming: () => {
          return LDClient.variation(LDFlags.shouldBlockSignups);
        },
        isSignupUnavailable: () => {
          // membership is blocked for all utilities
          return LDClient.variation(LDFlags.shouldBlockSignups);
        },
      },
      services: {
        fetchEnrollmentOptions: async context => {
          const { billpayEligibile } = await fetchBillpayEligibility({
            utilityId: context.funnelPacket!.utility.id,
          });
          const enrollmentOptions: EnrollmentDataSource[] = billpayEligibile
            ? ['utility_connect']
            : ['none'];
          return { enrollmentOptions };
        },
        fetchPromoCode: async context => {
          const { id, promoCode } = context.funnelPacket!.user;
          if (!promoCode) return { sponsored: false, sponsoredBy: '' };
          const { sponsored, sponsoredBy } = await fetchMembershipPromoCode({
            promoCode,
            userId: id,
          });
          return { sponsored, sponsoredBy };
        },
        signUpUser: async context => {
          await signUpUser({
            billingExperienceType: context.billingSelection!,
            enrollmentDataSourceName:
              context.enrollmentSelection! ||
              context.funnelPacket!.utilityAccount.enrollmentDataSourceName ||
              'none',
            planId: context.funnelPacket!.customPlan.id,
            track: 'membership',
            utilityAccountId: context.funnelPacket!.utilityAccount.id,
          });
          return undefined;
        },
        billingMachine: billingMachine(),
        enrollmentMachine: enrollmentMachine(),
        userMachine: userMachine(),
        utilityMachine: utilityMachine(),
      },
    },
  );
